/**
 * One Stock source over three providers (MK-5).
 *
 * Plan decision D1: the picker offers "Stock", not "Pexels / Unsplash /
 * Pixabay". One search fans out to every provider that has a key, the results
 * interleave, and each card names where it came from. Five tabs became two
 * segments in MK-4b; this is what makes the second one a single source rather
 * than a chooser.
 *
 * THREE RULES THIS FILE EXISTS TO KEEP, and all three are failure modes a
 * naive merge would introduce:
 *
 *  1. **One provider failing must not blank the results.** A rate-limited
 *     Unsplash key used to cost the user only Unsplash; after a merge it could
 *     cost them everything. Per-provider errors are COLLECTED and reported as
 *     a note; only an all-fail throws.
 *  2. **Attribution renders for every provider actually queried.** Pexels and
 *     Unsplash both require it by their terms, and a merge is an easy way to
 *     lose it — the picker used to show one line because one provider was
 *     active. `attributionLine()` answers for the set that ran.
 *  3. **The provider's own `download()` still runs.** Unsplash's
 *     download-tracking call is required by its API guidelines and lives in
 *     that adapter; the aggregator delegates rather than re-implementing, so
 *     it cannot be lost.
 */

import { pexels } from './pexels.js';
import { unsplash } from './unsplash.js';
import { pixabayPhotos } from './pixabay.js';

/** Query order, and the round-robin order results interleave in. */
export const STOCK_PROVIDERS = [
  { key: 'pexels', adapter: pexels },
  { key: 'unsplash', adapter: unsplash },
  { key: 'pixabay', adapter: pixabayPhotos }
];

/** The providers this account can actually query right now. */
export function providersWithKeys() {
  return STOCK_PROVIDERS.filter(({ adapter }) => {
    try {
      return !!adapter.getApiKey();
    } catch {
      // A storage failure is not a configured key.
      return false;
    }
  });
}

/**
 * One vocabulary for three adapters that disagree.
 *
 * They already emit `AUTH` / `RATE` / `NO_KEY`, but not from the same inputs:
 * all three map 401 to AUTH, Pexels and Pixabay map 429 to RATE, and Unsplash
 * maps **403** to RATE because that is what it actually returns when a demo
 * key runs out. Normalising HERE rather than in the adapters keeps each
 * adapter's own error surface intact for its direct callers (the SVG library
 * uses `pixabayVectors` on its own) while giving the merge one set to reason
 * about.
 */
export function normalizeProviderError(err) {
  const code = String(err?.code || '');
  if (code === 'NO_KEY' || code === 'AUTH' || code === 'RATE') return code;
  if (err?.name === 'AbortError') return 'ABORT';
  if (err?.name === 'TimeoutError') return 'TIMEOUT';
  return 'NETWORK';
}

const NOTE_FOR = {
  AUTH: (name) => `${name} rejected its API key`,
  RATE: (name) => `${name} is rate limited`,
  TIMEOUT: (name) => `${name} timed out`,
  NETWORK: (name) => `${name} could not be reached`,
  NO_KEY: (name) => `${name} has no API key`
};

/**
 * Round-robin by RANK, not by provider. Taking the first N of each in turn
 * keeps every provider represented in the first screenful; concatenating would
 * make the second and third provider invisible until the user paged, which is
 * the merge silently becoming a preference.
 */
export function interleaveByRank(lists) {
  const out = [];
  const depth = Math.max(0, ...lists.map((l) => l.length));
  for (let rank = 0; rank < depth; rank += 1) {
    for (const list of lists) if (rank < list.length) out.push(list[rank]);
  }
  return out;
}

/** `provider:id`, plus the thumbnail, so a re-page cannot double a card. */
function dedupe(items) {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const keys = [`${item.provider}:${item.id}`, item.thumb].filter(Boolean);
    if (keys.some((k) => seen.has(k))) continue;
    for (const k of keys) seen.add(k);
    out.push(item);
  }
  return out;
}

export const stockPhotos = {
  name: 'Stock',
  needsKey: true,
  // No key of its own: the three providers keep their own storage, and their
  // Settings rows are unchanged. `setApiKey` is a no-op rather than absent so
  // any caller that assumes the adapter shape does not throw.
  lsKey: null,
  attribution: 'Photos provided by Pexels, Unsplash and Pixabay',
  keyHelp: 'Add a free Pexels, Unsplash or Pixabay key in Settings → Integrations.',

  getApiKey() {
    // Non-empty when ANY provider is configured — one key is enough to search.
    return providersWithKeys().length > 0 ? 'configured' : '';
  },
  setApiKey() {},

  /** The line the picker renders, for the providers that actually ran. */
  attributionLine(queried = providersWithKeys()) {
    // Each provider's OWN wording, joined — not a sentence of our own, because
    // the wording is the part their terms ask for.
    return queried.map(({ adapter }) => adapter.attribution).join(' · ');
  },

  async search(query, page, signal) {
    const live = providersWithKeys();
    if (!live.length) {
      throw Object.assign(
        new Error('No stock API key. Add a Pexels, Unsplash or Pixabay key in Settings.'),
        { code: 'NO_KEY' }
      );
    }

    const settled = await Promise.allSettled(
      live.map(({ adapter }) => adapter.search(query, page, signal))
    );

    const lists = [];
    const notes = [];
    const codes = [];
    /** Providers with at least one result ON SCREEN — see the return below. */
    const contributed = [];
    let total = 0;
    settled.forEach((outcome, i) => {
      const entry = live[i];
      const { key, adapter } = entry;
      if (outcome.status === 'fulfilled') {
        const data = outcome.value || {};
        total += Number(data.total) || 0;
        const mapped = (data.results || []).map((result) => ({
          ...result,
          // Namespaced, because two providers can both answer with id "12".
          id: `${key}:${result.id}`,
          provider: key,
          providerName: adapter.name
        }));
        if (mapped.length) contributed.push(entry);
        lists.push(mapped);
        return;
      }
      const code = normalizeProviderError(outcome.reason);
      codes.push(code);
      // An abort is the picker superseding its own search, not a provider
      // problem — reporting it would put a note on screen for every keystroke.
      if (code !== 'ABORT') notes.push((NOTE_FOR[code] || NOTE_FOR.NETWORK)(adapter.name));
    });

    // Every provider failed → this IS the error state, and it wears the code
    // the picker already knows how to render. A partial failure is a note.
    if (!lists.length) {
      const first = codes.find((c) => c !== 'ABORT');
      if (!first) throw Object.assign(new Error('Search aborted'), { name: 'AbortError' });
      throw Object.assign(new Error(notes.join('; ') || 'Stock search failed'), { code: first });
    }

    return {
      results: dedupe(interleaveByRank(lists)),
      total,
      notes,
      queried: live.map(({ key }) => key),
      // Attribution follows the RESULTS, not the keys. A provider that was
      // asked and answered with nothing — or answered 403 — has no images on
      // screen, and crediting it would be a claim about pictures that are not
      // there. The TOS requirement is to attribute what you display; the merge
      // must not drop that, and must not invent it either.
      attribution: this.attributionLine(contributed)
    };
  },

  /**
   * Delegate to the provider that produced the item. This is what keeps
   * Unsplash's download-tracking call — required by its API guidelines and
   * implemented in that adapter — running after the merge.
   */
  async download(item) {
    const entry = STOCK_PROVIDERS.find(({ key }) => key === item?.provider);
    if (!entry) {
      throw Object.assign(new Error('This result has no provider. Try another image.'), {
        code: 'INVALID_URL'
      });
    }
    return entry.adapter.download(item);
  },

  /** Only if the owning provider has one; the CORS fallback is opt-in. */
  getSourceUrl(item) {
    const entry = STOCK_PROVIDERS.find(({ key }) => key === item?.provider);
    return typeof entry?.adapter.getSourceUrl === 'function'
      ? entry.adapter.getSourceUrl(item)
      : '';
  }
};
